/* istanbul ignore file */
import { inject, Injectable } from '@angular/core';
import { AuthService, UserAuthService } from '@libs/shared/auth';
import { CustomerModel } from '@libs/account/models';
import { AuthToken } from '@libs/shared/models';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  from,
  map,
  Observable,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DebuggerAuthState } from '../debugger.model';
import { OptionsService } from '../services';

@Injectable({ providedIn: 'root' })
export class AuthDebuggerService {
  private readonly token$: Observable<AuthToken | undefined>;
  private readonly customerNumber$: Observable<string>;
  private readonly updating$: BehaviorSubject<boolean>;

  private readonly authService: AuthService = inject(AuthService);

  public constructor(
    private readonly options: OptionsService,
    private readonly userAuthService: UserAuthService,
  ) {
    this.updating$ = new BehaviorSubject<boolean>(false);
    this.customerNumber$ = this.options.get('customerNumber');

    const reloadCustomers$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    this.token$ = this.options.get('bffEnv').pipe(
      switchMap(() => this.userAuthService.readToken()),
      delay(10),
      shareReplay(1),
    );

    const customers$: Observable<CustomerModel[] | undefined> = reloadCustomers$.pipe(
      take(1),
      withLatestFrom(this.token$),
      switchMap(([shouldLoad, token]) => {
        /* istanbul ignore else */
        if (shouldLoad) {
          this.authService.clearCustomers();

          if (token) {
            return from(this.authService.loadCustomers()).pipe(
              switchMap(() => this.authService.getAllCustomers()),
            );
          }
        }

        return this.authService.getAllCustomers();
      }),
    );

    customers$.subscribe();
  }

  public getIsUpdating(): Observable<boolean> {
    return this.updating$.pipe(
      distinctUntilChanged(),
      debounceTime(10),
    );
  }

  public waitUpdated(): Observable<void> {
    return this.getIsUpdating().pipe(
      filter(updating => !updating),
      map(() => void {}),
    );
  }

  public getCustomers(): Observable<CustomerModel[] | undefined> {
    return this.authService.getAllCustomers();
  }

  public getAuthState(): Observable<DebuggerAuthState> {
    return combineLatest({
      token: this.token$,
      customerNumber: this.customerNumber$,
    });
  }

  public updateAuthState(state: DebuggerAuthState): Observable<void> {
    return this.userAuthService.updateToken(state.token).pipe(
      tap(() => void this.options.update({
        customerNumber: state.customerNumber,
      })),
      map(() => {
        /* empty */
      }),
    );
  }
}
